// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import getRealTimeWarehouseInventory from "@insite/client-framework/Store/Data/RealTimeInventory/Handlers/GetRealTimeWarehouseInventory";
import translate from "@insite/client-framework/Translate";
import {
    AvailabilityDto,
    AvailabilityMessageType,
    ProductSettingsModel,
    WarehouseDto,
} from "@insite/client-framework/Types/ApiModels";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import Home from "@insite/mobius/Icons/Home";
import Mail from "@insite/mobius/Icons/Mail";
import Phone from "@insite/mobius/Icons/Phone";
import { Clickable } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ContactTypography from "@rugby/App/Components/Common/ContactTypography";
import CheckmarkRound from "@rugby/XDL/Icons/CheckmarkRound";
import FastTruck from "@rugby/XDL/Icons/FastTruck";
import { MediaQuery } from "@rugby/XDL/Utilities";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

interface OwnProps {
    productId: string;
    availability?: any;
    unitOfMeasure: string;
    trackInventory: boolean;
    isProductDetailsPage?: boolean;
    failedToLoadInventory?: boolean;
    extendedStyles?: ProductAvailabilityStyles;
    // XNG-Change
    trackQty?: boolean;
    qtyOnHand?: number;
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
    showInventory: state.context.showInventory,
    session: getSession(state),
});

const mapDispatchToProps = {
    getRealTimeWarehouseInventory,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductAvailabilityStyles {
    container?: GridContainerProps;
    messageGridItem?: GridItemProps;
    availabilityByWarehouseLinkGridItem?: GridItemProps;
    inventoryMessage?: InjectableCss;
    messageText?: TypographyPresentationProps;
    messageClickable?: InjectableCss;
    realTimeText?: TypographyPresentationProps;
    errorWrapper?: InjectableCss;
    errorText?: TypographyPresentationProps;
    availabilityByWarehouseLink?: LinkPresentationProps;
    availabilityByWarehouseModal?: ModalPresentationProps;
    contactUsDetailsModal?: ModalPresentationProps;
    modalAddressText?: TypographyPresentationProps;
    modalPhoneText?: TypographyPresentationProps;
    modalEmailText?: TypographyPresentationProps;
    centeringWrapper?: InjectableCss;
    modalAddressWrapper?: InjectableCss;
    modalPhoneWrapper?: InjectableCss;
    modalEmailWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    availabilityByWarehouseTable?: DataTableProps;
    availabilityByWarehouseTableHead?: DataTableHeadProps;
    availabilityByWarehouseTableHeader?: DataTableHeaderProps;
    availabilityByWarehouseTableBody?: DataTableBodyProps;
    availabilityByWarehouseTableRow?: DataTableRowProps;
    availabilityByWarehouseTableCell?: DataTableCellBaseProps;
}

export const productAvailabilityStyles: ProductAvailabilityStyles = {
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
        `,
    },
    messageGridItem: {
        width: 12,
    },
    availabilityByWarehouseLinkGridItem: { width: 12 },
    availabilityByWarehouseModal: {
        size: 400,
        cssOverrides: {
            modalContent: css`
                padding: 0;
            `,
            modalTitle: css`
                padding: 10px 10px 10px 20px;
            `,
            headlineTypography: css`
                font-size: 20px;
                margin-bottom: 0;
            `,
        },
    },
    contactUsDetailsModal: {
        size: 700,
        cssOverrides: {
            modalContent: css`
                && {
                    padding: 0px 40px 20px;
                    ${MediaQuery.lte("sm")} {
                        padding: 0px 20px 20px;
                    }
                }
            `,
            modalTitle: css`
                padding: 40px 40px 16px;
                ${MediaQuery.lte("sm")} {
                    padding-top: 0;
                }
            `,
        },
    },
    centeringWrapper: {
        css: css`
            height: 100px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: 0 auto;
        `,
    },
    messageText: {
        css: css`
            width: 100%;
            font-size: 14px;
            line-height: 20px;
            ${wrapInContainerStyles}
        `,
    },
    messageClickable: {
        css: css`
            @media print {
                display: flex !important;
            }
        `,
    },
    realTimeText: {
        weight: "bold",
        css: css`
            display: inline-block;
            text-align: left;
            min-width: 25px;

            &::after {
                overflow: hidden;
                display: inline-block;
                vertical-align: bottom;
                animation: ellipsis steps(4, end) 900ms infinite 1s;
                content: "\\2026";
                width: 0;
            }

            @keyframes ellipsis {
                to {
                    width: 1.25em;
                }
            }
        `,
    },
    errorText: {
        color: "danger",
        css: css`
            width: 100%;
            word-wrap: break-word;
        `,
    },
    modalAddressWrapper: {
        css: css`
            display: flex;
            align-items: center;
            overflow: hidden;
            padding: 16px 0;
        `,
    },
    modalPhoneWrapper: {
        css: css`
            display: flex;
            align-items: center;
            overflow: hidden;
            padding: 16px 0;
        `,
    },
    modalEmailWrapper: {
        css: css`
            display: flex;
            align-items: center;
            overflow: hidden;
            padding: 16px 0;
        `,
    },
    modalAddressText: {
        css: css`
            margin-bottom: 0;
            margin-left: 15px;
        `,
    },
    modalPhoneText: {
        css: css`
            margin-bottom: 0;
            margin-left: 15px;
        `,
    },
    modalEmailText: {
        css: css`
            margin-bottom: 0;
            margin-left: 15px;
        `,
    },
};

const InventoryMessage = styled.div<{ color: string }>`
    display: flex;
    align-items: center;
    padding: 0;
    border-radius: 0;
    background-color: transparent;
    ${IconWrapper} {
        margin-right: 4px;
    }
`;

const getAvailabilityColor = (productSettings: ProductSettingsModel, availability?: any) => {
    if (!productSettings.showInventoryAvailability) {
        return "common";
    }

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return "danger";
    }

    if (availabilityType === AvailabilityMessageType.LowStock) {
        return "warning";
    }

    return "success";
};

const getAvailabilityIcon = (productSettings: ProductSettingsModel, availability?: any) => {
    if (!productSettings.showInventoryAvailability) {
        return undefined;
    }

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return FastTruck;
    }

    if (availabilityType === AvailabilityMessageType.LowStock) {
        return AlertTriangle;
    }

    return CheckmarkRound;
};

const getAvailabilityIconSize = (productSettings: ProductSettingsModel, availability?: any) => {
    if (!productSettings.showInventoryAvailability) {
        return undefined;
    }

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return 16;
    }
    return 12;
};

const showLink = (
    availability: any,
    trackInventory: boolean,
    productSettings: ProductSettingsModel,
    isProductDetailsPage?: boolean,
): boolean => {
    if (
        !availability ||
        availability.messageType === AvailabilityMessageType.NoMessage ||
        !trackInventory ||
        !productSettings.showInventoryAvailability ||
        !productSettings.displayInventoryPerWarehouse
    ) {
        return false;
    }

    if (!productSettings.displayInventoryPerWarehouseOnlyOnProductDetail) {
        return true;
    }

    return !!isProductDetailsPage;
};

const ProductAvailability = ({
    productId,
    availability,
    unitOfMeasure,
    trackInventory,
    productSettings,
    session,
    isProductDetailsPage,
    getRealTimeWarehouseInventory,
    failedToLoadInventory,
    showInventory,
    extendedStyles,
    // XNG-Change
    trackQty = false,
    qtyOnHand,
}: Props) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [warehouses, setWarehouses] = useState<WarehouseDto[] | undefined>(undefined);
    const [errorMessage, setErrorMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [contactUsModalIsOpen, setContactUsModalIsOpen] = useState(false);
    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };
    const viewAvailabilityByWarehouseClickHandler = () => {
        setIsLoading(true);
        setModalIsOpen(true);
        getRealTimeWarehouseInventory({
            productId,
            unitOfMeasure,
            expand: ["warehouses"],
            onComplete: result => {
                setWarehouses(result.warehouses);
                setErrorMessage(result.errorMessage);
                setIsLoading(false);
            },
        });
    };

    const color = getAvailabilityColor(productSettings, availability);
    const iconSrc = getAvailabilityIcon(productSettings, availability);
    const iconSize = getAvailabilityIconSize(productSettings, availability);

    const [styles] = useState(() => mergeToNew(productAvailabilityStyles, extendedStyles));

    if (!showInventory) {
        return null;
    }

    const sessionDefaultWarehouse = JSON.parse(session?.properties?.defaultWarehouse || "{}");

    const contactUsModalOpenHandler = () => {
        setContactUsModalIsOpen(true);
    };

    const contactUsModalCloseHandler = () => {
        setContactUsModalIsOpen(false);
    };

    if (failedToLoadInventory || availability?.noInventoryFound) {
        return (
            <StyledWrapper {...styles.errorWrapper} className="inventory-load-failed">
                <Typography {...styles.errorText}>{siteMessage("RealTimeInventory_InventoryLoadFailed")}</Typography>
            </StyledWrapper>
        );
    }

    const openChatPopup = () => {
        (window as any).acquireIO.ui.status("max");
    };

    let inventoryTextComponent = <Typography {...styles.realTimeText} />;

    // condition changed in the last update , we need to check it if we need to change anything in our blueprint!
    if (availability && availability.message) {
        if (availability.messageType === AvailabilityMessageType.OutOfStock) {
            inventoryTextComponent = (
                <InventoryMessage color={color} {...styles.inventoryMessage}>
                    <Icon color={color} src={iconSrc} size={iconSize} width={iconSize} height={iconSize} />
                    <Clickable
                        onClick={contactUsModalOpenHandler}
                        data-test-selector="availabilityMessage"
                        {...styles.messageClickable}
                    >
                        {trackQty ? (
                            <Typography color={color} {...styles.messageText} data-test-selector="availabilityMessage">
                                {siteMessage("Rugby_Out_Of_Stock_Text")}
                            </Typography>
                        ) : (
                            <Typography color={color} {...styles.messageText} data-test-selector="availabilityMessage">
                                {availability.actualAmountMessage || availability.message}
                            </Typography>
                        )}
                    </Clickable>
                </InventoryMessage>
            );
        } else {
            inventoryTextComponent = (
                <InventoryMessage color={color} {...styles.inventoryMessage}>
                    <Icon color={color} src={iconSrc} width={12} height={12} />
                    {trackQty && qtyOnHand ? (
                        <Typography color={color} {...styles.messageText} data-test-selector="availabilityMessage">
                            {(siteMessage("Rugby_In_Stock_Text") as string).replace("{0}", `${qtyOnHand}`)}
                        </Typography>
                    ) : (
                        <Typography color={color} {...styles.messageText} data-test-selector="availabilityMessage">
                            {availability.actualAmountMessage || availability.message}
                        </Typography>
                    )}
                </InventoryMessage>
            );
        }
    } else if (availability) {
        inventoryTextComponent = <></>;
    }

    return (
        <GridContainer {...styles.container} className="productAvailabilityContainer">
            <GridItem {...styles.messageGridItem}>{inventoryTextComponent}</GridItem>
            {showLink(availability, trackInventory, productSettings, isProductDetailsPage) && (
                <GridItem {...styles.availabilityByWarehouseLinkGridItem}>
                    <Link
                        {...styles.availabilityByWarehouseLink}
                        onClick={viewAvailabilityByWarehouseClickHandler}
                        data-test-selector="availabilityByWarehouseLink"
                    >
                        {translate("View Availability by Warehouse")}
                    </Link>
                    <Modal
                        {...styles.availabilityByWarehouseModal}
                        headline={translate("Warehouse Inventory")}
                        isOpen={modalIsOpen}
                        handleClose={modalCloseHandler}
                    >
                        {isLoading && (
                            <StyledWrapper {...styles.centeringWrapper}>
                                <LoadingSpinner {...styles.spinner} />
                            </StyledWrapper>
                        )}
                        {!isLoading && (
                            <>
                                {errorMessage ? (
                                    <div>{errorMessage}</div>
                                ) : (
                                    <DataTable
                                        {...styles.availabilityByWarehouseTable}
                                        data-test-selector="availabilityByWarehouseModal"
                                    >
                                        <DataTableHead {...styles.availabilityByWarehouseTableHead}>
                                            <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                                {translate("Warehouse")}
                                            </DataTableHeader>
                                            <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                                {translate("Qty")}
                                            </DataTableHeader>
                                        </DataTableHead>
                                        <DataTableBody {...styles.availabilityByWarehouseTableBody}>
                                            {warehouses?.map(warehouse => (
                                                <DataTableRow
                                                    {...styles.availabilityByWarehouseTableRow}
                                                    key={warehouse.name}
                                                >
                                                    <DataTableCell
                                                        {...styles.availabilityByWarehouseTableCell}
                                                        data-test-selector="warehouseAvailabilityModal_Description"
                                                    >
                                                        {warehouse.description || warehouse.name}
                                                    </DataTableCell>
                                                    <DataTableCell
                                                        {...styles.availabilityByWarehouseTableCell}
                                                        data-test-selector="warehouseAvailabilityModal_Quantity"
                                                    >
                                                        {warehouse.qty}
                                                    </DataTableCell>
                                                </DataTableRow>
                                            ))}
                                        </DataTableBody>
                                    </DataTable>
                                )}
                            </>
                        )}
                    </Modal>
                </GridItem>
            )}
            <Modal
                headline={translate("Contact Us to Find Available Stock")}
                {...styles.contactUsDetailsModal}
                isOpen={contactUsModalIsOpen}
                handleClose={contactUsModalCloseHandler}
            >
                {sessionDefaultWarehouse.address1 && (
                    <StyledWrapper {...styles.modalAddressWrapper}>
                        <Icon src={Home} color="primary" />
                        <Typography {...styles.modalAddressText} variant="h6">
                            {sessionDefaultWarehouse.address1}
                        </Typography>
                    </StyledWrapper>
                )}
                {sessionDefaultWarehouse.phone && (
                    <StyledWrapper {...styles.modalPhoneWrapper}>
                        <Icon src={Phone} color="primary" />
                        <Typography {...styles.modalPhoneText} variant="h6">
                            Call Your Branch:{" "}
                            <ContactTypography targetValue={sessionDefaultWarehouse.phone} type="phone">
                                {sessionDefaultWarehouse.phone}
                            </ContactTypography>
                        </Typography>
                    </StyledWrapper>
                )}
                {sessionDefaultWarehouse.email && (
                    <StyledWrapper {...styles.modalEmailWrapper}>
                        <Icon src={Mail} color="primary" />
                        <Typography {...styles.modalEmailText} variant="h6">
                            Email Your Branch:{" "}
                            <ContactTypography targetValue={sessionDefaultWarehouse.email} type="email">
                                {sessionDefaultWarehouse.email}
                            </ContactTypography>
                        </Typography>
                    </StyledWrapper>
                )}
            </Modal>
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductAvailability);
